import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CATEGORY_KEYS,
  CURRENCY_MAP,
  DOMAIN_KEYS,
  MOST_RECENT_SORT_ORDER,
  SORT_ORDERS,
} from '../../../shared/constants';
import { FormControl, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { Router, ActivatedRoute, NavigationEnd, Event, RouterLink } from '@angular/router';
import { SearchService } from '../services/search.service';
import { ResponsiveService } from '../services/responsive.service';
import { SidenavService } from '../services/sidenav.service';
import { EventService } from '../services/event.service';
import { DomainKey } from '../../../shared/types/product';
import { MatSelectionList, MatListModule } from '@angular/material/list';
import { filter, first } from 'rxjs/operators';
import { getCurrencySymbol } from '@angular/common';
import * as T from '../../../shared/translations';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
    selector: 'app-sidenav',
    templateUrl: './sidenav.component.html',
    styleUrls: ['./sidenav.component.scss'],
    standalone: true,
    imports: [
      ReactiveFormsModule,
      MatFormFieldModule,
      MatInputModule,
      MatSelectModule,
      MatListModule,
      MatButtonModule,
      MatIconModule,
      RouterLink,
    ]
})
export class SidenavComponent implements AfterViewInit {
  readonly selectionList = viewChild.required<MatSelectionList>('selectionList');
  showBoundMatSelectionList = true;
  categories = CATEGORY_KEYS['1'];
  sortOrders = SORT_ORDERS;
  categoriesControl = new FormControl<number[]>([]);
  searchForm = this.fb.group({
    search: [undefined],
    sort: ['0'],
    minNew: [undefined],
    maxNew: [undefined],
    minUsed: [undefined],
    maxUsed: [undefined],
    minSavings: [undefined],
    minDifference: [undefined],
  });
  isSmallScreen = false;
  domain: DomainKey = '1';
  currencyMap = CURRENCY_MAP;
  getCurrencySymbol = getCurrencySymbol;
  T = T;
  private currentUrl = '';
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private searchService: SearchService,
    private ref: ChangeDetectorRef,
    private responsiveService: ResponsiveService,
    private sidenavService: SidenavService,
    private eventService: EventService
  ) { }

  ngAfterViewInit() {
    this.router.events
      .pipe(
        filter((e: Event): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
        first()
      )
      .subscribe((e: NavigationEnd) => {
        this.activatedRoute.queryParams
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((params) => {
            this.currentUrl = this.router.url;
            const domainParam = params['domain'];
            if (domainParam && DOMAIN_KEYS.indexOf(domainParam) >= 0) {
              this.domain = domainParam;
              this.categories = CATEGORY_KEYS[this.domain];
              this.ref.markForCheck();
            } else if (this.domain !== '1') {
              this.domain = '1';
              this.categories = CATEGORY_KEYS[this.domain];
              this.ref.markForCheck();
            }
            // https://github.com/angular/components/issues/15477
            const cat = params.cat;
            this.showBoundMatSelectionList = false;
            this.ref.detectChanges();
            if (cat) {
              this.categoriesControl.setValue(this.binaryStringToCategories(
                cat,
                this.domain
              ));
            } else {
              this.setDefaultSelectedCategories();
            }
            this.showBoundMatSelectionList = true;

            for (const key of Object.keys(params)) {
              this.setFormControlValue(key, params[key]);
            }

            this.sortOrders = [...SORT_ORDERS, MOST_RECENT_SORT_ORDER];
            this.ref.markForCheck();
            this.ref.detectChanges();
          });
      });
    this.eventService.resetForms$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.resetForm());
  }

  onSortChanged(event: MatSelectChange) {
    this.submitForm(true);
  }

  submitForm(sortChange?: boolean) {
    const cat = this.categoriesToBinary(this.domain);
    const values = { cat, domain: this.domain, ...this.searchForm.value };
    let dealUrl = '/deals';
    this.router.navigate([dealUrl], {
      relativeTo: this.activatedRoute,
      queryParams: { ...values },
    });
    if (this.responsiveService.isSmallScreen() && !sortChange) {
      this.sidenavService.close();
    }
  }

  resetForm() {
    this.setDefaultSelectedCategories();
    this.searchForm = this.fb.group({
      search: [undefined],
      sort: ['0'],
      minNew: [undefined],
      maxNew: [undefined],
      minUsed: [undefined],
      maxUsed: [undefined],
      minSavings: [undefined],
      minDifference: [undefined],
    });
  }

  setFormControlValue(formControlName: string, value: any) {
    if (this.searchForm.contains(formControlName) && value != null) {
      this.searchForm.get(formControlName)?.setValue(value);
    }
  }

  categoriesToBinary(domain: DomainKey) {
    let res = Array(CATEGORY_KEYS[domain].length + 1).join('0');
    for (const i of this.categoriesControl.value!) {
      if (i < 0 || i >= res.length) {
        continue;
      }
      res = res.substring(0, i) + '1' + res.substring(i + 1);
    }
    return res;
  }

  binaryStringToCategories(categories: any, domain: DomainKey): number[] {
    if (
      categories == null ||
      typeof categories !== 'string' ||
      categories.length > CATEGORY_KEYS[domain].length
    ) {
      return [];
    }
    const result = [];
    for (let i = 0; i < categories.length; i++) {
      if (categories.charAt(i) !== '1') {
        continue;
      }
      result.push(i);
    }
    return result;
  }

  setDefaultSelectedCategories() {
    this.categoriesControl.setValue([]);
  }
}
